import { compileMatcher, findMatchesInText, type CompiledMatcher } from "./matcher";
import { TooltipView } from "../ui/tooltip";
import { selectArticleRules } from "../shared/article-rule-selection";
import { normalizeSelectors } from "../shared/selectors";
import { enforceAmazonAffiliateTag, isSafeHttpUrl, normalizeAmazonAffiliateTag } from "../shared/url-safety";
import type {
  EngineStats,
  MatchContext,
  MatchEvent,
  MatchRule,
  ProductData,
  RulesPayload,
  RulesSource,
  TooltipConfig,
  TooltipEngine
} from "../types";

interface MatchRecord {
  id: string;
  element: HTMLElement;
  key: string;
  selector: string;
  ruleId: string;
  ruleType: "keyword" | "regex";
  sourcePattern: string;
  matchedText: string;
  normalizedMatch: string;
  occurrence: number;
}

interface CacheEntry {
  status: "loading" | "ready" | "empty" | "error";
  product?: ProductData;
  promise?: Promise<ProductData | null>;
}

interface RulesCacheRecord {
  version: string;
  generatedAt?: string;
  expiresAt: number;
  rules: MatchRule[];
}

interface RuntimeConfig extends TooltipConfig {
  selectors: string[];
  defaultMaxMatchesPerRule: number;
  maxTotalMatches: number;
  maxMatchesPerKeyword: number;
  maxMatchesPerParagraph: number;
  openInNewTab: boolean;
}

interface ResolvedRulesSet {
  version: string | null;
  source: RulesSource;
  rules: MatchRule[];
}

const MATCH_SELECTOR = "[data-pt-match-id]";
const SKIPPED_TAGS = new Set(["script", "style", "noscript", "textarea", "input", "code", "pre"]);
const SKIPPED_ANCESTOR_SELECTOR = "a";
const DEFAULT_RULES_CACHE_KEY = "pt-rules-cache-v1";
const DEFAULT_RULES_TTL_SECONDS = 300;
const SESSION_ID_KEY = "pt-session-id-v1";
const DEFAULT_MAX_TOTAL_MATCHES = 40;
const DEFAULT_MAX_MATCHES_PER_KEYWORD = 1;
const DEFAULT_MAX_MATCHES_PER_PARAGRAPH = 2;
const PARAGRAPH_BLOCK_SELECTOR = "p, li, blockquote";
const FOCUS_VISIBILITY_THRESHOLD = 0.5;

export class ProductTooltipRuntime implements TooltipEngine {
  private readonly config: RuntimeConfig;
  private matcher: CompiledMatcher | null = null;
  private resolvedRules: MatchRule[] = [];
  private readonly usageByRule = new Map<string, number>();
  private readonly occurrenceByRule = new Map<string, number>();
  private readonly recordsByElement = new WeakMap<HTMLElement, MatchRecord>();
  private readonly recordsById = new Map<string, MatchRecord>();
  private readonly elementByKey = new Map<string, Set<HTMLElement>>();
  private readonly matchCountByKeyword = new Map<string, number>();
  private readonly matchCountByParagraph = new Map<HTMLElement, number>();
  private readonly disabledKeys = new Set<string>();
  private readonly cache = new Map<string, CacheEntry>();
  private readonly tooltip: TooltipView;
  private readonly sessionId: string;
  private readonly eventQueue: MatchEvent[] = [];
  private readonly focusObserver: IntersectionObserver | null;
  private readonly focusedMatchIds = new Set<string>();

  private bootPromise: Promise<void> | null = null;
  private flushEventsTimer: number | null = null;
  private matchIdCounter = 0;
  private activeMatchId: string | null = null;
  private activeRecord: MatchRecord | null = null;
  private activeProduct: ProductData | null = null;
  private activeToken = 0;
  private hideTimer: number | null = null;
  private destroyed = false;
  private rulesVersion: string | null = null;
  private rulesSource: RulesSource = "none";

  private readonly stats: EngineStats = {
    scannedNodes: 0,
    totalMatches: 0,
    activeMatches: 0,
    resolvedProducts: 0,
    failedProducts: 0,
    disabledMatches: 0,
    queuedEvents: 0,
    rulesVersion: null,
    rulesSource: "none"
  };

  private readonly onPointerOverBound = (event: Event) => {
    void this.onPointerOver(event as PointerEvent);
  };

  private readonly onPointerOutBound = (event: Event) => {
    this.onPointerOut(event as PointerEvent);
  };

  private readonly onFocusInBound = (event: Event) => {
    void this.onFocusIn(event as FocusEvent);
  };

  private readonly onFocusOutBound = (event: Event) => {
    this.onFocusOut(event as FocusEvent);
  };

  private readonly onClickBound = (event: Event) => {
    void this.onClick(event as MouseEvent);
  };

  private readonly onKeyDownBound = (event: Event) => {
    void this.onKeyDown(event as KeyboardEvent);
  };

  private readonly onPageHideBound = () => {
    this.flushEvents();
  };

  private readonly onVisibilityChangeBound = () => {
    if (document.visibilityState === "hidden") {
      this.flushEvents();
    }
  };

  private readonly onMatchVisibilityBound: IntersectionObserverCallback = (entries, observer) => {
    for (const entry of entries) {
      if (!entry.isIntersecting || entry.intersectionRatio < FOCUS_VISIBILITY_THRESHOLD) {
        continue;
      }

      const target = entry.target;
      if (!(target instanceof HTMLElement)) {
        continue;
      }

      observer.unobserve(target);

      const record = this.recordsByElement.get(target);
      if (!record || this.disabledKeys.has(record.key)) {
        continue;
      }

      if (this.focusedMatchIds.has(record.id)) {
        continue;
      }

      this.focusedMatchIds.add(record.id);
      this.enqueueEvent("focus", record);
    }
  };

  constructor(config: TooltipConfig) {
    this.config = {
      ...config,
      selectors: normalizeSelectors(config.selectors),
      defaultMaxMatchesPerRule: config.defaultMaxMatchesPerRule ?? 3,
      maxTotalMatches: clampPositiveInteger(config.maxTotalMatches, DEFAULT_MAX_TOTAL_MATCHES),
      maxMatchesPerKeyword: clampPositiveInteger(config.maxMatchesPerKeyword, DEFAULT_MAX_MATCHES_PER_KEYWORD),
      maxMatchesPerParagraph: clampPositiveInteger(
        config.maxMatchesPerParagraph,
        DEFAULT_MAX_MATCHES_PER_PARAGRAPH
      ),
      openInNewTab: config.openInNewTab ?? true
    };

    validateConfig(this.config);

    this.sessionId = getOrCreateSessionId();
    this.tooltip = new TooltipView(this.config.tooltipTheme, this.config.openInNewTab);
    this.focusObserver = this.createFocusObserver();

    this.attachEventListeners();

    const staticRules = hasUsableRules(this.config.rules) ? this.config.rules : null;
    if (staticRules) {
      this.applyRules(staticRules, "static", null);
    }

    if (this.config.rulesProvider || !staticRules) {
      this.bootPromise = this.bootstrapRules(Boolean(staticRules));
    }
  }

  destroy(): void {
    if (this.destroyed) {
      return;
    }

    this.destroyed = true;
    this.flushEvents();
    this.detachEventListeners();
    this.clearHideTimer();
    this.clearEventsTimer();
    this.tooltip.destroy();
    this.unwrapMatches();
    this.recordsById.clear();
    this.elementByKey.clear();
    this.matchCountByKeyword.clear();
    this.matchCountByParagraph.clear();
    this.cache.clear();
    this.usageByRule.clear();
    this.occurrenceByRule.clear();
    this.focusedMatchIds.clear();
    this.focusObserver?.disconnect();
  }

  refresh(): void {
    if (this.destroyed) {
      return;
    }

    if (this.resolvedRules.length > 0) {
      this.tryApplyRules(this.resolvedRules, this.rulesSource, this.rulesVersion);
      return;
    }

    if (!this.matcher) {
      if (!this.bootPromise) {
        this.bootPromise = this.bootstrapRules(false);
      }
      return;
    }

    this.resetDecoratedState();
    this.scanDocument();
  }

  getStats(): EngineStats {
    this.stats.queuedEvents = this.eventQueue.length;
    this.stats.rulesVersion = this.rulesVersion;
    this.stats.rulesSource = this.rulesSource;
    return { ...this.stats };
  }

  private async bootstrapRules(hasStaticBaseline: boolean): Promise<void> {
    try {
      if (this.config.rulesProvider) {
        const providerRules = await this.loadRulesFromProvider();
        if (providerRules && !this.destroyed) {
          if (this.tryApplyRules(providerRules.rules, providerRules.source, providerRules.version)) {
            return;
          }
        }

        if (hasStaticBaseline || this.destroyed) {
          return;
        }
      }

      if (this.destroyed || this.matcher) {
        return;
      }

      const fallback = this.loadFallbackRules();
      if (!fallback) {
        return;
      }

      this.tryApplyRules(fallback.rules, fallback.source, fallback.version);
    } finally {
      this.bootPromise = null;
    }
  }

  private async loadRulesFromProvider(): Promise<ResolvedRulesSet | null> {
    if (!this.config.rulesProvider) {
      return null;
    }

    try {
      const payload = await this.config.rulesProvider();
      if (!payload || !isValidRulesPayload(payload)) {
        return null;
      }

      persistRulesCache(this.getRulesCacheKey(), payload);
      return {
        source: "provider",
        version: payload.version,
        rules: payload.rules
      };
    } catch {
      return null;
    }
  }

  private loadFallbackRules(): ResolvedRulesSet | null {
    const staticRules = hasUsableRules(this.config.rules) ? this.config.rules : null;
    if (staticRules) {
      return {
        source: "static",
        version: null,
        rules: staticRules
      };
    }

    const cached = readRulesCache(this.getRulesCacheKey());
    if (cached && hasUsableRules(cached.rules)) {
      return {
        source: "cache",
        version: cached.version,
        rules: cached.rules
      };
    }

    const starterPack = hasUsableRules(this.config.starterPack) ? this.config.starterPack : null;
    if (starterPack) {
      return {
        source: "starter-pack",
        version: null,
        rules: starterPack
      };
    }

    return null;
  }

  private applyRules(rules: MatchRule[], source: RulesSource, version: string | null): void {
    this.resolvedRules = rules;
    this.matcher = compileMatcher(this.buildRuntimeRules(rules), this.config.defaultMaxMatchesPerRule);
    this.rulesSource = source;
    this.rulesVersion = version;
    this.stats.rulesSource = source;
    this.stats.rulesVersion = version;

    this.resetDecoratedState();
    this.scanDocument();
  }

  private tryApplyRules(rules: MatchRule[], source: RulesSource, version: string | null): boolean {
    try {
      this.applyRules(rules, source, version);
      return true;
    } catch {
      return false;
    }
  }

  private getRulesCacheKey(): string {
    return this.config.rulesCacheKey ?? DEFAULT_RULES_CACHE_KEY;
  }

  private buildRuntimeRules(rules: MatchRule[]): MatchRule[] {
    if (this.config.selectorProfile !== "article") {
      return rules;
    }

    const articleContext = this.collectArticleContext();
    const selection = selectArticleRules({
      rules,
      contentBlocks: articleContext.contentBlocks,
      contextBlocks: articleContext.contextBlocks
    });

    return selection.selectedRules.length > 0 ? selection.selectedRules : rules;
  }

  private collectArticleContext(): { contentBlocks: string[]; contextBlocks: string[] } {
    const contentBlocks: string[] = [];
    const contextBlocks: string[] = [];
    const seenRoots = new Set<HTMLElement>();
    const contextContainers = new Set<HTMLElement>();

    for (const selector of this.config.selectors) {
      const roots = Array.from(document.querySelectorAll<HTMLElement>(selector));
      for (const root of roots) {
        if (!seenRoots.has(root)) {
          seenRoots.add(root);
          const text = normalizeBlockText(root.textContent);
          if (text) {
            contentBlocks.push(text);
          }
        }

        const container =
          root.closest<HTMLElement>("#adv__parsed__content, article, main, [role='main'], section") ??
          root.parentElement ??
          root;
        contextContainers.add(container);
      }
    }

    for (const container of contextContainers) {
      const headings = Array.from(container.querySelectorAll<HTMLElement>("h1, h2, h3"));
      for (const heading of headings) {
        const text = normalizeBlockText(heading.textContent);
        if (text) {
          contextBlocks.push(text);
        }
      }
    }

    return { contentBlocks, contextBlocks };
  }

  private attachEventListeners(): void {
    document.addEventListener("pointerover", this.onPointerOverBound, { passive: true });
    document.addEventListener("pointerout", this.onPointerOutBound, { passive: true });
    document.addEventListener("focusin", this.onFocusInBound);
    document.addEventListener("focusout", this.onFocusOutBound);
    document.addEventListener("click", this.onClickBound);
    document.addEventListener("keydown", this.onKeyDownBound);
    document.addEventListener("visibilitychange", this.onVisibilityChangeBound);
    window.addEventListener("pagehide", this.onPageHideBound);

    this.tooltip.element.addEventListener("pointerenter", () => {
      this.clearHideTimer();
    });

    this.tooltip.element.addEventListener("pointerleave", () => {
      this.scheduleHide();
    });
  }

  private detachEventListeners(): void {
    document.removeEventListener("pointerover", this.onPointerOverBound);
    document.removeEventListener("pointerout", this.onPointerOutBound);
    document.removeEventListener("focusin", this.onFocusInBound);
    document.removeEventListener("focusout", this.onFocusOutBound);
    document.removeEventListener("click", this.onClickBound);
    document.removeEventListener("keydown", this.onKeyDownBound);
    document.removeEventListener("visibilitychange", this.onVisibilityChangeBound);
    window.removeEventListener("pagehide", this.onPageHideBound);
  }

  private scanDocument(): void {
    if (!this.matcher) {
      return;
    }

    const visitedTextNodes = new Set<Text>();

    for (const selector of this.config.selectors) {
      const roots = Array.from(document.querySelectorAll<HTMLElement>(selector));
      for (const root of roots) {
        const textNodes = collectTextNodes(root);
        for (const textNode of textNodes) {
          if (visitedTextNodes.has(textNode)) {
            continue;
          }
          visitedTextNodes.add(textNode);
          this.stats.scannedNodes += 1;
          this.decorateTextNode(textNode, selector);
        }
      }
    }

    this.stats.activeMatches = this.recordsById.size;
  }

  private decorateTextNode(textNode: Text, selector: string): void {
    if (!this.matcher) {
      return;
    }

    const value = textNode.nodeValue ?? "";
    if (!value.trim()) {
      return;
    }

    const matches = findMatchesInText(value, this.matcher, this.usageByRule);
    if (matches.length === 0) {
      return;
    }

    const fragment = document.createDocumentFragment();
    const decoratedElements: HTMLElement[] = [];
    let cursor = 0;
    const paragraph = resolveParagraphElement(textNode);

    for (const match of matches) {
      if (match.start > cursor) {
        fragment.appendChild(document.createTextNode(value.slice(cursor, match.start)));
      }

      if (this.recordsById.size >= this.config.maxTotalMatches) {
        fragment.appendChild(document.createTextNode(value.slice(match.start)));
        cursor = value.length;
        break;
      }

      const keywordCount = this.matchCountByKeyword.get(match.normalizedMatch) ?? 0;
      if (keywordCount >= this.config.maxMatchesPerKeyword) {
        fragment.appendChild(document.createTextNode(match.text));
        cursor = match.end;
        continue;
      }

      const paragraphCount = paragraph ? (this.matchCountByParagraph.get(paragraph) ?? 0) : 0;
      if (paragraph && paragraphCount >= this.config.maxMatchesPerParagraph) {
        fragment.appendChild(document.createTextNode(match.text));
        cursor = match.end;
        continue;
      }

      const element = document.createElement("span");
      const recordId = `${this.matchIdCounter}`;
      this.matchIdCounter += 1;

      element.className = "pt-match";
      element.textContent = match.text;
      element.setAttribute("data-pt-match-id", recordId);
      element.setAttribute("tabindex", "0");
      element.setAttribute("role", "link");
      element.setAttribute("aria-haspopup", "dialog");

      const key = `${match.rule.id}::${match.normalizedMatch}`;
      const record: MatchRecord = {
        id: recordId,
        element,
        key,
        selector,
        ruleId: match.rule.id,
        ruleType: match.rule.type,
        sourcePattern: match.sourcePattern,
        matchedText: match.text,
        normalizedMatch: match.normalizedMatch,
        occurrence: this.nextOccurrence(match.rule.id)
      };

      this.recordsByElement.set(element, record);
      this.recordsById.set(recordId, record);
      this.addElementToKey(key, element);
      this.incrementKeywordCount(match.normalizedMatch);
      this.incrementParagraphCount(paragraph);
      this.stats.totalMatches += 1;
      fragment.appendChild(element);
      decoratedElements.push(element);

      cursor = match.end;
    }

    if (cursor < value.length) {
      fragment.appendChild(document.createTextNode(value.slice(cursor)));
    }

    textNode.replaceWith(fragment);

    for (const element of decoratedElements) {
      this.observeMatchElement(element);

      const record = this.recordsByElement.get(element);
      if (record) {
        this.enqueueEvent("deployed", record);
      }
    }
  }

  private async onPointerOver(event: PointerEvent): Promise<void> {
    const target = findMatchElement(event.target);
    if (!target) {
      return;
    }

    await this.activateMatch(target);
  }

  private onPointerOut(event: PointerEvent): void {
    const target = findMatchElement(event.target);
    if (!target) {
      return;
    }

    const relatedTarget = event.relatedTarget as Node | null;
    if (relatedTarget && (target.contains(relatedTarget) || this.tooltip.contains(relatedTarget))) {
      return;
    }

    this.scheduleHide();
  }

  private async onFocusIn(event: FocusEvent): Promise<void> {
    const target = findMatchElement(event.target);
    if (!target) {
      return;
    }

    await this.activateMatch(target);
  }

  private onFocusOut(event: FocusEvent): void {
    const target = findMatchElement(event.target);
    if (!target) {
      return;
    }

    const relatedTarget = event.relatedTarget as Node | null;
    if (relatedTarget && this.tooltip.contains(relatedTarget)) {
      return;
    }

    this.scheduleHide();
  }

  private async onClick(event: MouseEvent): Promise<void> {
    const tooltipLink = (event.target as Element | null)?.closest<HTMLAnchorElement>("a[data-role='tooltip-link']");
    if (tooltipLink) {
      event.preventDefault();
      if (this.activeRecord) {
        const fallbackProduct: ProductData = {
          url: tooltipLink.href,
          title: tooltipLink.textContent?.trim() || "Product"
        };
        this.enqueueEvent("click", this.activeRecord, this.activeProduct ?? fallbackProduct);
      }
      this.navigate(tooltipLink.href);
      return;
    }

    const matchElement = findMatchElement(event.target);
    if (!matchElement) {
      return;
    }

    event.preventDefault();
    const record = this.recordsByElement.get(matchElement);
    if (!record || this.disabledKeys.has(record.key)) {
      return;
    }

    const product = await this.resolveProduct(record);
    if (!product) {
      this.disableKey(record.key);
      this.tooltip.hide();
      return;
    }

    this.enqueueEvent("click", record, product);
    this.navigate(product.url);
  }

  private async onKeyDown(event: KeyboardEvent): Promise<void> {
    if (event.key === "Escape") {
      this.tooltip.hide();
      return;
    }

    const target = findMatchElement(event.target);
    if (!target) {
      return;
    }

    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    const record = this.recordsByElement.get(target);
    if (!record || this.disabledKeys.has(record.key)) {
      return;
    }

    const product = await this.resolveProduct(record);
    if (!product) {
      this.disableKey(record.key);
      this.tooltip.hide();
      return;
    }

    this.enqueueEvent("click", record, product);
    this.navigate(product.url);
  }

  private async activateMatch(element: HTMLElement): Promise<void> {
    const record = this.recordsByElement.get(element);
    if (!record || this.disabledKeys.has(record.key)) {
      return;
    }

    this.clearHideTimer();
    this.activeMatchId = record.id;
    const currentToken = ++this.activeToken;
    this.activeRecord = record;
    this.activeProduct = null;

    const cached = this.cache.get(record.key);
    if (cached?.status !== "ready") {
      this.tooltip.showLoading(record.element);
    }

    const product = await this.resolveProduct(record);
    if (!product) {
      this.disableKey(record.key);
      this.tooltip.hide();
      return;
    }

    if (this.activeToken !== currentToken || this.activeMatchId !== record.id) {
      return;
    }

    this.activeRecord = record;
    this.activeProduct = product;
    this.tooltip.show(record.element, product, this.config.openInNewTab);
    this.enqueueEvent("impression", record, product);
  }

  private async resolveProduct(record: MatchRecord): Promise<ProductData | null> {
    if (this.disabledKeys.has(record.key)) {
      return null;
    }

    const cached = this.cache.get(record.key);
    if (cached?.status === "ready") {
      return cached.product ?? null;
    }
    if (cached?.status === "empty" || cached?.status === "error") {
      return null;
    }
    if (cached?.status === "loading" && cached.promise) {
      return cached.promise;
    }

    const context: MatchContext = {
      ruleId: record.ruleId,
      ruleType: record.ruleType,
      matchedText: record.matchedText,
      normalizedMatch: record.normalizedMatch,
      selector: record.selector,
      occurrence: record.occurrence,
      sourcePattern: record.sourcePattern
    };

    const promise = this.config
      .fetchProduct(context)
      .then((result) => {
        const normalized = normalizeProduct(result);
        if (!normalized) {
          this.cache.set(record.key, { status: "empty" });
          this.stats.failedProducts += 1;
          return null;
        }

        this.cache.set(record.key, { status: "ready", product: normalized });
        this.stats.resolvedProducts += 1;
        return normalized;
      })
      .catch(() => {
        this.cache.set(record.key, { status: "error" });
        this.stats.failedProducts += 1;
        return null;
      });

    this.cache.set(record.key, { status: "loading", promise });
    return promise;
  }

  private addElementToKey(key: string, element: HTMLElement): void {
    const bucket = this.elementByKey.get(key);
    if (bucket) {
      bucket.add(element);
      return;
    }
    this.elementByKey.set(key, new Set([element]));
  }

  private incrementKeywordCount(keyword: string): void {
    const next = (this.matchCountByKeyword.get(keyword) ?? 0) + 1;
    this.matchCountByKeyword.set(keyword, next);
  }

  private incrementParagraphCount(paragraph: HTMLElement | null): void {
    if (!paragraph) {
      return;
    }

    const next = (this.matchCountByParagraph.get(paragraph) ?? 0) + 1;
    this.matchCountByParagraph.set(paragraph, next);
  }

  private nextOccurrence(ruleId: string): number {
    const nextValue = (this.occurrenceByRule.get(ruleId) ?? 0) + 1;
    this.occurrenceByRule.set(ruleId, nextValue);
    return nextValue;
  }

  private disableKey(key: string): void {
    if (this.disabledKeys.has(key)) {
      return;
    }

    this.disabledKeys.add(key);
    const elements = this.elementByKey.get(key);
    if (!elements) {
      return;
    }

    for (const element of elements) {
      this.focusObserver?.unobserve(element);

      const recordId = element.getAttribute("data-pt-match-id");
      if (recordId) {
        this.focusedMatchIds.delete(recordId);
        this.recordsById.delete(recordId);
      }

      const textNode = document.createTextNode(element.textContent ?? "");
      element.replaceWith(textNode);
      const parent = textNode.parentElement;
      if (parent) {
        parent.normalize();
      }
      this.stats.disabledMatches += 1;
    }

    this.elementByKey.delete(key);
    this.stats.activeMatches = this.recordsById.size;
  }

  private navigate(url: string): void {
    if (this.config.openInNewTab) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    window.location.assign(url);
  }

  private observeMatchElement(element: HTMLElement): void {
    this.focusObserver?.observe(element);
  }

  private createFocusObserver(): IntersectionObserver | null {
    if (!this.config.eventsReporter || typeof window === "undefined" || typeof window.IntersectionObserver !== "function") {
      return null;
    }

    return new window.IntersectionObserver(this.onMatchVisibilityBound, {
      threshold: [FOCUS_VISIBILITY_THRESHOLD]
    });
  }

  private enqueueEvent(eventType: MatchEvent["eventType"], record: MatchRecord, product?: ProductData): void {
    if (!this.config.eventsReporter) {
      return;
    }

    if ((eventType === "impression" || eventType === "click") && !product) {
      return;
    }

    this.eventQueue.push({
      eventType,
      sessionId: this.sessionId,
      ruleId: record.ruleId,
      normalizedMatch: record.normalizedMatch,
      matchedText: record.matchedText,
      selector: record.selector,
      timestamp: new Date().toISOString(),
      sourceDomain: typeof window !== "undefined" ? window.location.hostname : undefined,
      productUrl: product?.url,
      productTitle: product?.title,
      sourceRulesVersion: this.rulesVersion,
      tenantId: this.config.tenantId,
      locale: this.config.locale,
      selectorProfile: this.config.selectorProfile,
      vertical: this.config.vertical
    });

    this.stats.queuedEvents = this.eventQueue.length;
    if (eventType === "click") {
      this.flushEvents();
      return;
    }
    this.scheduleEventFlush();
  }

  private scheduleEventFlush(): void {
    if (!this.config.eventsReporter || this.flushEventsTimer !== null) {
      return;
    }

    this.flushEventsTimer = window.setTimeout(() => {
      this.flushEvents();
    }, 180);
  }

  private flushEvents(): void {
    if (!this.config.eventsReporter || this.eventQueue.length === 0) {
      this.clearEventsTimer();
      return;
    }

    const batch = this.eventQueue.splice(0, this.eventQueue.length);
    this.stats.queuedEvents = this.eventQueue.length;
    this.clearEventsTimer();

    try {
      void this.config.eventsReporter(batch)
        .catch(() => {
          // Non-blocking telemetry path: keep UX deterministic even if reporting fails.
        });
    } catch {
      // Non-blocking telemetry path: keep UX deterministic even if reporting fails.
    }
  }

  private scheduleHide(): void {
    this.clearHideTimer();
    this.hideTimer = window.setTimeout(() => {
      this.tooltip.hide();
      this.activeMatchId = null;
      this.activeRecord = null;
      this.activeProduct = null;
    }, 100);
  }

  private clearHideTimer(): void {
    if (this.hideTimer === null) {
      return;
    }
    window.clearTimeout(this.hideTimer);
    this.hideTimer = null;
  }

  private clearEventsTimer(): void {
    if (this.flushEventsTimer === null) {
      return;
    }

    window.clearTimeout(this.flushEventsTimer);
    this.flushEventsTimer = null;
  }

  private resetDecoratedState(): void {
    this.tooltip.hide();
    this.clearHideTimer();

    this.activeMatchId = null;
    this.activeRecord = null;
    this.activeProduct = null;

    this.unwrapMatches();
    this.recordsById.clear();
    this.elementByKey.clear();
    this.matchCountByKeyword.clear();
    this.matchCountByParagraph.clear();
    this.disabledKeys.clear();
    this.cache.clear();
    this.usageByRule.clear();
    this.occurrenceByRule.clear();
    this.focusedMatchIds.clear();
    this.focusObserver?.disconnect();

    this.stats.scannedNodes = 0;
    this.stats.totalMatches = 0;
    this.stats.activeMatches = 0;
    this.stats.resolvedProducts = 0;
    this.stats.failedProducts = 0;
    this.stats.disabledMatches = 0;
  }

  private unwrapMatches(): void {
    const elements = Array.from(document.querySelectorAll<HTMLElement>(MATCH_SELECTOR));
    for (const element of elements) {
      const textNode = document.createTextNode(element.textContent ?? "");
      element.replaceWith(textNode);
      const parent = textNode.parentElement;
      if (parent) {
        parent.normalize();
      }
    }
  }
}

function validateConfig(config: TooltipConfig): void {
  if (!Array.isArray(config.selectors) || config.selectors.length === 0) {
    throw new Error("selectors must contain at least one CSS selector.");
  }

  const hasAnyRulesSource =
    hasUsableRules(config.rules) || hasUsableRules(config.starterPack) || typeof config.rulesProvider === "function";

  if (!hasAnyRulesSource) {
    throw new Error("Provide at least one rules source: rules, rulesProvider, or starterPack.");
  }

  if (typeof config.fetchProduct !== "function") {
    throw new Error("fetchProduct must be a function returning a Promise.");
  }

  if (config.maxTotalMatches !== undefined && (!Number.isFinite(config.maxTotalMatches) || config.maxTotalMatches <= 0)) {
    throw new Error("maxTotalMatches must be a positive number.");
  }

  if (
    config.maxMatchesPerKeyword !== undefined &&
    (!Number.isFinite(config.maxMatchesPerKeyword) || config.maxMatchesPerKeyword <= 0)
  ) {
    throw new Error("maxMatchesPerKeyword must be a positive number.");
  }

  if (
    config.maxMatchesPerParagraph !== undefined &&
    (!Number.isFinite(config.maxMatchesPerParagraph) || config.maxMatchesPerParagraph <= 0)
  ) {
    throw new Error("maxMatchesPerParagraph must be a positive number.");
  }
}

function resolveParagraphElement(textNode: Text): HTMLElement | null {
  const parent = textNode.parentElement;
  if (!parent) {
    return null;
  }

  return parent.closest(PARAGRAPH_BLOCK_SELECTOR);
}

function collectTextNodes(root: HTMLElement): Text[] {
  const nodes: Text[] = [];

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      const text = node.nodeValue ?? "";
      if (!text.trim()) {
        return NodeFilter.FILTER_REJECT;
      }

      const parent = node.parentElement;
      if (!parent) {
        return NodeFilter.FILTER_REJECT;
      }

      if (parent.closest(MATCH_SELECTOR)) {
        return NodeFilter.FILTER_REJECT;
      }

      if (parent.closest(SKIPPED_ANCESTOR_SELECTOR)) {
        return NodeFilter.FILTER_REJECT;
      }

      const tagName = parent.tagName.toLowerCase();
      if (SKIPPED_TAGS.has(tagName)) {
        return NodeFilter.FILTER_REJECT;
      }

      return NodeFilter.FILTER_ACCEPT;
    }
  });

  let current = walker.nextNode();
  while (current) {
    nodes.push(current as Text);
    current = walker.nextNode();
  }

  return nodes;
}

function findMatchElement(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) {
    return null;
  }

  return target.closest<HTMLElement>(MATCH_SELECTOR);
}

function normalizeProduct(result: ProductData | null): ProductData | null {
  if (!result || typeof result.url !== "string" || !isSafeHttpUrl(result.url)) {
    return null;
  }

  const affiliateTagApplied = normalizeAmazonAffiliateTag(result.affiliateTagApplied);
  const url = affiliateTagApplied ? enforceAmazonAffiliateTag(result.url, affiliateTagApplied) : result.url;
  const title = typeof result.title === "string" && result.title.trim() ? result.title.trim() : "Product";
  const affiliateTagSource =
    result.affiliateTagSource === "internal" ||
    result.affiliateTagSource === "customer" ||
    result.affiliateTagSource === "external"
      ? result.affiliateTagSource
      : undefined;

  return {
    url,
    title,
    displayMode: result.displayMode === "cta-only" ? "cta-only" : "full",
    imageUrl: sanitizeOptional(result.imageUrl),
    price: sanitizeOptional(result.price),
    originalPrice: sanitizeOptional(result.originalPrice),
    discountLabel: sanitizeOptional(result.discountLabel),
    vendor: sanitizeOptional(result.vendor),
    description: sanitizeOptional(result.description),
    ctaLabel: sanitizeOptional(result.ctaLabel),
    affiliateTagApplied,
    affiliateTagSource
  };
}

function sanitizeOptional(value: string | undefined): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function hasUsableRules(rules: MatchRule[] | undefined): rules is MatchRule[] {
  return Array.isArray(rules) && rules.length > 0;
}

function clampPositiveInteger(value: number | undefined, fallback: number): number {
  if (!Number.isFinite(value) || !value || value <= 0) {
    return fallback;
  }

  return Math.floor(value);
}

function normalizeBlockText(value: string | null | undefined): string {
  return value?.replace(/\s+/g, " ").trim() ?? "";
}

function isValidRulesPayload(payload: RulesPayload): boolean {
  return typeof payload.version === "string" && payload.version.length > 0 && hasUsableRules(payload.rules);
}

function persistRulesCache(cacheKey: string, payload: RulesPayload): void {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }

  const ttlSeconds = payload.ttlSeconds > 0 ? payload.ttlSeconds : DEFAULT_RULES_TTL_SECONDS;
  const record: RulesCacheRecord = {
    version: payload.version,
    generatedAt: payload.generatedAt,
    expiresAt: Date.now() + ttlSeconds * 1000,
    rules: payload.rules
  };

  try {
    window.localStorage.setItem(cacheKey, JSON.stringify(record));
  } catch {
    // Ignore quota/security issues for cache storage.
  }
}

function readRulesCache(cacheKey: string): RulesCacheRecord | null {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(cacheKey);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<RulesCacheRecord>;
    if (
      typeof parsed.version !== "string" ||
      typeof parsed.expiresAt !== "number" ||
      !Array.isArray(parsed.rules)
    ) {
      return null;
    }

    if (Date.now() > parsed.expiresAt) {
      window.localStorage.removeItem(cacheKey);
      return null;
    }

    return {
      version: parsed.version,
      generatedAt: parsed.generatedAt,
      expiresAt: parsed.expiresAt,
      rules: parsed.rules as MatchRule[]
    };
  } catch {
    return null;
  }
}

function getOrCreateSessionId(): string {
  if (typeof window === "undefined") {
    return "server-session";
  }

  try {
    const existing = window.sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) {
      return existing;
    }

    const generated = generateSessionId();
    window.sessionStorage.setItem(SESSION_ID_KEY, generated);
    return generated;
  } catch {
    return generateSessionId();
  }
}

function generateSessionId(): string {
  const randomPart = Math.random().toString(36).slice(2, 10);
  return `pt-${Date.now().toString(36)}-${randomPart}`;
}
